import React, { useState, useEffect, useRef } from "react";
import { lookupMember, markAttendance, registerMember } from "../lib/membership";
import { OnScreenKeyboard } from "../components/OnScreenKeyboard";
import { Delete, ArrowRight, RotateCcw, LogIn, LogOut, HelpCircle, PartyPopper } from "lucide-react";

const IDLE_MS = 30000;
const RESULT_MS = 3500;
const big = "h-20 md:h-24 [@media(max-height:850px)]:h-16 px-10 rounded-3xl font-display font-black text-3xl md:text-4xl flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-30";

const fmtDob = (d) => {
  const p = d.padEnd(8, "_");
  return `${p.slice(0, 2)} / ${p.slice(2, 4)} / ${p.slice(4)}`;
};
const toIso = (d) => {
  if (d.length !== 8) return null;
  const [dd, mm, yy] = [+d.slice(0, 2), +d.slice(2, 4), +d.slice(4)];
  const dt = new Date(Date.UTC(yy, mm - 1, dd));
  if (yy < 1900 || dt.getUTCDate() !== dd || dt.getUTCMonth() !== mm - 1 || dt > new Date()) return null;
  return `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
};

function NumPad({ value, onChange, max }) {
  const k = "h-16 md:h-20 [@media(max-height:850px)]:h-14 rounded-2xl bg-[#18181B] border border-white/10 font-display font-black text-4xl active:scale-90 active:bg-[#FF007F] flex items-center justify-center";
  const add = (n) => value.length < max && onChange(value + n);
  return (
    <div data-testid="numpad" className="grid grid-cols-3 gap-3 w-full max-w-md mx-auto select-none">
      {"123456789".split("").map((n) => <button key={n} data-testid={`num-${n}`} onClick={() => add(n)} className={k}>{n}</button>)}
      <button data-testid="num-clear" onClick={() => onChange("")} className={`${k} text-xl`}>CLEAR</button>
      <button data-testid="num-0" onClick={() => add("0")} className={k}>0</button>
      <button data-testid="num-back" onClick={() => onChange(value.slice(0, -1))} className={k}><Delete className="w-9 h-9" /></button>
    </div>
  );
}

function Shell({ children, onRestart, showRestart }) {
  return (
    <div data-testid="selfscreen" className="h-screen w-full bg-[#09090B] text-white relative overflow-hidden flex flex-col select-none">
      <img src="/yaz-wall.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative z-10 flex items-center justify-between px-6 md:px-8 pt-4 shrink-0">
        <img src="/yaz-logo.webp" alt="YAZ" className="h-12 md:h-16 w-auto" />
        {showRestart && <button data-testid="selfscreen-restart" onClick={onRestart} className="h-14 md:h-16 px-5 rounded-2xl bg-white/10 font-display font-bold text-lg md:text-xl flex items-center gap-2"><RotateCcw className="w-6 h-6" /> START AGAIN</button>}
      </div>
      <div className="relative z-10 flex-1 min-h-0 overflow-y-auto px-4 md:px-6 flex flex-col">
        <div className="my-auto w-full flex flex-col items-center py-4">{children}</div>
      </div>
      <p data-testid="selfscreen-help" className="relative z-10 shrink-0 text-center text-[#A1A1AA] font-display font-bold text-lg md:text-2xl py-3 flex items-center justify-center gap-2"><HelpCircle className="w-7 h-7 text-[#00F0FF]" /> Need help? Ask a member of staff</p>
    </div>
  );
}

const Title = ({ children, sub }) => (
  <div className="text-center mb-4">
    <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight">{children}</h1>
    {sub && <p className="text-[#A1A1AA] text-xl md:text-2xl mt-2 font-semibold">{sub}</p>}
  </div>
);
const Field = ({ testid, children }) => (
  <div data-testid={testid} className="w-full max-w-3xl min-h-[4.5rem] md:min-h-[5.5rem] mb-4 rounded-3xl bg-[#18181B] border-2 border-[#FF007F] flex items-center justify-center px-6 font-display font-black text-3xl md:text-5xl break-all text-center">{children}</div>
);

export default function SelfScreen() {
  const [step, setStep] = useState("name");
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [parent, setParent] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const idle = useRef(null);

  const restart = () => {
    setStep("name"); setName(""); setDob(""); setEmail(""); setPhone(""); setParent("");
    setResult(null); setError(""); setBusy(false);
  };

  useEffect(() => {
    clearTimeout(idle.current);
    if (step === "result") idle.current = setTimeout(restart, RESULT_MS);
    else if (step !== "name" || name) idle.current = setTimeout(restart, IDLE_MS);
    return () => clearTimeout(idle.current);
  }, [step, name, dob, email, phone, parent]);

  const toggle = async (member, isNew = false) => {
    const type = member.status === "in" ? "out" : "in";
    await markAttendance(member.id, type);
    setResult({ type, name: member.name.split(" ")[0], isNew });
    setStep("result");
  };

  const submitDob = async () => {
    const iso = toIso(dob);
    if (!iso) { setError("That date doesn't look right — try DD MM YYYY"); return; }
    setBusy(true); setError("");
    try { await toggle(await lookupMember(name.trim(), iso)); }
    catch (e) {
      if (e.response?.status === 404) setStep("join-email");
      else setError("Something went wrong — please try again");
    } finally { setBusy(false); }
  };

  const submitJoin = async (parentPhone) => {
    setBusy(true); setError("");
    try {
      const m = await registerMember({ name: name.trim(), dob: toIso(dob), email: email.trim().toLowerCase(), phone, parentPhone });
      await toggle({ ...m, status: m.status || "out" }, true);
    } catch (e) { setError(e.response?.data?.detail || "Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const next = (testid, onClick, disabled, label = "NEXT") => (
    <button data-testid={testid} onClick={onClick} disabled={disabled || busy} className={`${big} bg-[#FF007F] mt-4 min-w-[280px]`}>{busy ? "…" : label} <ArrowRight className="w-9 h-9" /></button>
  );
  const err = error && <p data-testid="selfscreen-error" className="text-[#FF3B30] font-bold text-2xl md:text-3xl mb-4 text-center">{error}</p>;

  if (step === "result") {
    const inn = result.type === "in";
    return (
      <Shell>
        <div data-testid={`selfscreen-result-${result.type}`} className={`w-full max-w-4xl rounded-[3rem] p-14 text-center ${inn ? "bg-[#00FA9A] text-black" : "bg-[#FF007F] text-white"}`}>
          {result.isNew ? <PartyPopper className="w-28 h-28 mx-auto mb-4" /> : inn ? <LogIn className="w-28 h-28 mx-auto mb-4" /> : <LogOut className="w-28 h-28 mx-auto mb-4" />}
          <p className="font-display font-black text-6xl md:text-8xl">{inn ? `Welcome, ${result.name}!` : `Bye, ${result.name}!`}</p>
          <p className="font-display font-bold text-3xl md:text-4xl mt-4">{result.isNew ? "You're now a YAZ member and signed in" : inn ? "You're signed in" : "You're signed out — see you soon"}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell onRestart={restart} showRestart={step !== "name" || !!name}>
      {step === "name" && (
        <>
          <Title sub="Type your full name to sign in or out">What's your name?</Title>
          <Field testid="selfscreen-name">{name || <span className="text-[#52525B]">Your name</span>}</Field>
          <OnScreenKeyboard value={name} onChange={setName} capsAfterSpace />
          {next("selfscreen-name-next", () => { setError(""); setStep("dob"); }, name.trim().length < 2)}
        </>
      )}
      {step === "dob" && (
        <>
          <Title sub={`Hi ${name.trim().split(" ")[0]} — enter day, month, year`}>Your date of birth?</Title>
          <Field testid="selfscreen-dob">{fmtDob(dob)}</Field>
          {err}
          <NumPad value={dob} onChange={(v) => { setDob(v); setError(""); }} max={8} />
          {next("selfscreen-dob-go", submitDob, dob.length !== 8, "GO")}
        </>
      )}
      {step === "join-email" && (
        <>
          <Title sub={`We couldn't find you, ${name.trim().split(" ")[0]} — let's make your free membership`}>Your email?</Title>
          <Field testid="selfscreen-email">{email || <span className="text-[#52525B]">you@email.com</span>}</Field>
          <div className="flex gap-3 mb-3 w-full max-w-3xl">
            {["@", ".", ".com", ".co.uk", "@gmail.com"].map((s) => <button key={s} data-testid={`email-key-${s}`} onClick={() => setEmail(email + s)} className="flex-1 h-16 rounded-xl bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] font-display font-bold text-xl">{s}</button>)}
          </div>
          <OnScreenKeyboard value={email} onChange={setEmail} startCaps={false} />
          {next("selfscreen-email-next", () => setStep("join-phone"), !/^\S+@\S+\.\S+$/.test(email.trim()))}
        </>
      )}
      {step === "join-phone" && (
        <>
          <Title sub="Your mobile number">Your phone?</Title>
          <Field testid="selfscreen-phone">{phone || <span className="text-[#52525B]">07…</span>}</Field>
          <NumPad value={phone} onChange={setPhone} max={13} />
          {next("selfscreen-phone-next", () => setStep("join-parent"), phone.length < 10)}
        </>
      )}
      {step === "join-parent" && (
        <>
          <Title sub="Parent or guardian number (optional)">Parent's number?</Title>
          <Field testid="selfscreen-parent">{parent || <span className="text-[#52525B]">Optional</span>}</Field>
          {err}
          <NumPad value={parent} onChange={setParent} max={13} />
          <div className="flex gap-4 flex-wrap justify-center">
            <button data-testid="selfscreen-parent-skip" onClick={() => submitJoin("")} disabled={busy} className={`${big} bg-white/10 mt-4`}>SKIP</button>
            {next("selfscreen-join-submit", () => submitJoin(parent), parent.length > 0 && parent.length < 10, "JOIN & SIGN IN")}
          </div>
        </>
      )}
    </Shell>
  );
}
